import {
    createAsyncThunk,
    createSlice,
    PayloadAction,
    createSelector,
} from "@reduxjs/toolkit"
import type { AxiosError } from "axios"
import { liveWorkflowService } from "@/modules/onboarding/infrastructure/liveWorkflowService"
import type {
    WorkflowInstanceDto,
    LiveWorkflowsFilter,
    LiveWorkflowsResponse,
} from "../dtos/liveWorkflowDto"
import { RootState } from "@/store"
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice"

interface LiveWorkflowsState {
    items: WorkflowInstanceDto[]
    selectedInstance?: WorkflowInstanceDto | null
    filter: LiveWorkflowsFilter
    pagination: {
        currentPage: number
        pageSize: number
        totalCount: number
        totalPages: number
    }
    isLoading: boolean
    error?: string | null
}

const initialState: LiveWorkflowsState = {
    items: [],
    selectedInstance: null,
    filter: {
        page: 1,
        pageSize: 20,
    },
    pagination: {
        currentPage: 1,
        pageSize: 20,
        totalCount: 0,
        totalPages: 0,
    },
    isLoading: false,
    error: null,
}

const requireOrgId = (state: RootState) => {
    const orgId = selectCurrentOrganizationId(state)
    if (!orgId) throw new Error("No organisation selected")
    return orgId
}

const extractErrorMessage = (error: unknown): string => {
    const axiosError = error as AxiosError<{ error?: string }>
    return axiosError.response?.data?.error ?? (error as Error).message ?? "An unexpected error occurred"
}

export const fetchLiveWorkflows = createAsyncThunk<
    LiveWorkflowsResponse,
    LiveWorkflowsFilter | undefined,
    { state: RootState }
>("liveWorkflows/fetchAll", async (filter, { getState, rejectWithValue }) => {
    try {
        const orgId = requireOrgId(getState())
        const response = await liveWorkflowService.getLiveWorkflows(
            orgId,
            filter || {},
        )
        return response
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error))
    }
})

export const fetchWorkflowInstance = createAsyncThunk<
    WorkflowInstanceDto,
    string,
    { state: RootState }
>(
    "liveWorkflows/fetchInstance",
    async (instanceId, { getState, rejectWithValue }) => {
        try {
            const orgId = requireOrgId(getState())
            return await liveWorkflowService.getWorkflowInstance(orgId, instanceId)
        } catch (error) {
            return rejectWithValue(extractErrorMessage(error))
        }
    },
)

export const cancelWorkflowInstance = createAsyncThunk<
    string,
    { instanceId: string; reason?: string },
    { state: RootState }
>(
    "liveWorkflows/cancel",
    async ({ instanceId, reason }, { getState, rejectWithValue }) => {
        try {
            const orgId = requireOrgId(getState())
            await liveWorkflowService.cancelWorkflow(orgId, instanceId, reason)
            return instanceId
        } catch (error) {
            return rejectWithValue(extractErrorMessage(error))
        }
    },
)

export const reactivateWorkflowInstance = createAsyncThunk<
    string,
    string,
    { state: RootState }
>(
    "liveWorkflows/reactivate",
    async (instanceId, { getState, rejectWithValue }) => {
        try {
            const orgId = requireOrgId(getState())
            await liveWorkflowService.reactivateWorkflow(orgId, instanceId)
            return instanceId
        } catch (error) {
            return rejectWithValue(extractErrorMessage(error))
        }
    },
)

export const cancelTask = createAsyncThunk<
    { instanceId: string; taskId: string },
    { instanceId: string; taskId: string; reason?: string },
    { state: RootState }
>(
    "liveWorkflows/cancelTask",
    async ({ instanceId, taskId, reason }, { getState, rejectWithValue }) => {
        try {
            const orgId = requireOrgId(getState())
            await liveWorkflowService.cancelTask(orgId, instanceId, taskId, reason)
            return { instanceId, taskId }
        } catch (error) {
            return rejectWithValue(extractErrorMessage(error))
        }
    },
)

export const reactivateTask = createAsyncThunk<
    { instanceId: string; taskId: string },
    { instanceId: string; taskId: string },
    { state: RootState }
>(
    "liveWorkflows/reactivateTask",
    async ({ instanceId, taskId }, { getState, rejectWithValue }) => {
        try {
            const orgId = requireOrgId(getState())
            await liveWorkflowService.reactivateTask(orgId, instanceId, taskId)
            return { instanceId, taskId }
        } catch (error) {
            return rejectWithValue(extractErrorMessage(error))
        }
    },
)

export const deleteWorkflowInstance = createAsyncThunk<
    string,
    string,
    { state: RootState }
>("liveWorkflows/delete", async (instanceId, { getState, rejectWithValue }) => {
    try {
        const orgId = requireOrgId(getState())
        await liveWorkflowService.deleteWorkflowInstance(orgId, instanceId)
        return instanceId
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error))
    }
})

const liveWorkflowsSlice = createSlice({
    name: "liveWorkflows",
    initialState,
    reducers: {
        setFilter(state, action: PayloadAction<Partial<LiveWorkflowsFilter>>) {
            state.filter = { ...state.filter, ...action.payload }
        },
        setSelectedInstance(
            state,
            action: PayloadAction<WorkflowInstanceDto | null>,
        ) {
            state.selectedInstance = action.payload
        },
        clearError(state) {
            state.error = null
        },
        resetFilter(state) {
            state.filter = {
                page: 1,
                pageSize: 20,
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch live workflows
            .addCase(fetchLiveWorkflows.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchLiveWorkflows.fulfilled, (state, action) => {
                state.isLoading = false
                state.items = action.payload.items
                state.pagination = {
                    currentPage: action.payload.currentPage,
                    pageSize: action.payload.pageSize,
                    totalCount: action.payload.totalCount,
                    totalPages: action.payload.totalPages,
                }
            })
            .addCase(fetchLiveWorkflows.rejected, (state, action) => {
                state.isLoading = false
                state.error = (action.payload as string) || action.error.message || null
            })

            // Fetch single workflow instance
            .addCase(fetchWorkflowInstance.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchWorkflowInstance.fulfilled, (state, action) => {
                state.isLoading = false
                state.selectedInstance = action.payload

                const index = state.items.findIndex(
                    (item) => item.id === action.payload.id,
                )
                if (index !== -1) {
                    state.items[index] = action.payload
                }
            })
            .addCase(fetchWorkflowInstance.rejected, (state, action) => {
                state.isLoading = false
                state.error = (action.payload as string) || action.error.message || null
            })

            // Cancel workflow instance
            .addCase(cancelWorkflowInstance.fulfilled, () => {
                // Refetch will be triggered by the component
            })

            // Reactivate workflow instance
            .addCase(reactivateWorkflowInstance.fulfilled, () => {
                // Refetch will be triggered by the component
            })

            // Cancel task
            .addCase(cancelTask.fulfilled, () => {
                // Refetch will be triggered by the component
            })

            // Reactivate task
            .addCase(reactivateTask.fulfilled, () => {
                // Refetch will be triggered by the component
            })

            // Delete workflow instance
            .addCase(deleteWorkflowInstance.fulfilled, (state, action) => {
                // Remove from items list
                state.items = state.items.filter((item) => item.id !== action.payload)
                // Clear selected instance if it matches
                if (state.selectedInstance?.id === action.payload) {
                    state.selectedInstance = null
                }
                // Update pagination
                state.pagination.totalCount = Math.max(
                    0,
                    state.pagination.totalCount - 1,
                )
            })
    },
})

export const { setFilter, setSelectedInstance, clearError, resetFilter } =
    liveWorkflowsSlice.actions

export const selectLiveWorkflowsState = (state: RootState) =>
    state.liveWorkflows
export const selectLiveWorkflows = (state: RootState) =>
    state.liveWorkflows.items
export const selectSelectedWorkflowInstance = (state: RootState) =>
    state.liveWorkflows.selectedInstance
export const selectLiveWorkflowsFilter = (state: RootState) =>
    state.liveWorkflows.filter
export const selectLiveWorkflowsPagination = (state: RootState) =>
    state.liveWorkflows.pagination
export const selectLiveWorkflowsLoading = (state: RootState) =>
    state.liveWorkflows.isLoading
export const selectLiveWorkflowsError = (state: RootState) =>
    state.liveWorkflows.error

export const selectFilteredLiveWorkflows = createSelector(
    [selectLiveWorkflows, selectLiveWorkflowsFilter],
    (workflows, filter) => {
        if (!filter.search) return workflows

        const searchTerm = filter.search.toLowerCase()
        return workflows.filter(
            (workflow) =>
                workflow.workflowName.toLowerCase().includes(searchTerm) ||
                workflow.assignedToName.toLowerCase().includes(searchTerm) ||
                workflow.assignedToEmail.toLowerCase().includes(searchTerm) ||
                workflow.organizationName.toLowerCase().includes(searchTerm),
        )
    },
)

export default liveWorkflowsSlice.reducer
